import React, { useState, useCallback, useEffect, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import UploadPanel   from "../components/UploadPanel";
import StatusTracker from "../components/StatusTracker";
import ScanViewer    from "../components/ScanViewer";
import ResultsPanel  from "../components/ResultsPanel";
import IshaChat      from "../components/IshaChat";
import { AuthContext } from "../contexts/AuthContext";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

// Model-level evaluation metrics for the finalized Lung ResAttUNet
const LUNG_METRICS = [
  { label: "Dice Score",  value: 91.2 },
  { label: "Precision",   value: 93.8 },
  { label: "Recall",      value: 89.5 },
  { label: "F1 Score",    value: 91.6 },
  { label: "IoU",         value: 84.1 },
  { label: "FROC (CPM)",  value: 87.4 },
];

export default function LungCancerPage() {
  const [searchParams] = useSearchParams();
  const { token } = useContext(AuthContext);
  const [step,    setStep]    = useState("upload");
  const [jobId,   setJobId]   = useState(null);
  const [jobData, setJobData] = useState(null);
  const [showMetrics, setShowMetrics] = useState(false);
  const [loadingJob, setLoadingJob] = useState(false);
  const [goToSlice,  setGoToSlice]  = useState(null);

  // Auto-load a saved scan from ?job=<id> query parameter
  useEffect(() => {
    const qJob = searchParams.get("job");
    if (!qJob || jobId === qJob) return;
    setLoadingJob(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get(`${API_BASE}/api/job/${qJob}`, { headers })
      .then((res) => {
        const data = res.data;
        if (data.status === "COMPLETE") {
          setJobId(qJob);
          setJobData(data);
          setStep("results");
        } else {
          setJobId(qJob);
          setStep("processing");
        }
      })
      .catch(() => { /* job not found or unauthorized — stay on upload */ })
      .finally(() => setLoadingJob(false));
  }, [searchParams, token]);

  const handleUploadComplete = useCallback((newJobId) => {
    setJobId(newJobId);
    setStep("processing");
  }, []);

  const handleJobComplete = useCallback((resultData) => {
    setJobData(resultData);
    setStep("results");
  }, []);

  const handleReset = useCallback(() => {
    setStep("upload");
    setJobId(null);
    setJobData(null);
  }, []);

  return (
    <>
      <h1 className="h3 mb-3">
        <strong>Lung Cancer</strong> Dashboard
      </h1>

      <div className="row">
        <p className="text-left">
          Upload CT Scan in DICOM format and get analysis. We use a combination of Deep Learning
          (ResAttUNet) &amp; GPU-powered inference to give detailed nodule detection analysis.
          <br /><br />
          <b>Steps To Use:</b><br />
          1. Drag &amp; drop your CT scan (ZIP of DICOMs, single .dcm, or .mhd file) into the
          upload box, or click to browse.<br />
          2. Wait for upload to complete and job to be queued.<br />
          3. Our AI system will segment lung tissue and run U-Net inference on each CT slice.<br />
          4. View results in the interactive CT slice viewer — toggle between original and AI overlay.<br />
          5. Download annotated PNGs or DICOM with overlay.<br />
          6. See Evaluation Metrics for model confidence.<br />
          7. Ask Isha for more information.
          <br /><br />
          <b>Privacy Notes:</b>
          <ul style={{ listStyleType: "none" }}>
            <li>We don&apos;t store your data without your permission.</li>
            <li>
              We only give predictions through our AI-based Models; once our models analyze
              the scan, we delete it across our servers.
            </li>
            <li>Always provide feedback after getting analysis.</li>
          </ul>
        </p>
      </div>

      {/* ── Main workflow ─────────────────────────────────────────────────── */}
      <div className="lung-ct-workflow">
        {loadingJob && (
          <div style={{ textAlign: "center", padding: "3rem" }}>
            <div className="spinner" style={{ width: 32, height: 32, margin: "0 auto 1rem" }} />
            <p style={{ color: "var(--text-muted)" }}>Loading saved analysis…</p>
          </div>
        )}

        {!loadingJob && step === "upload" && (
          <UploadPanel onUploadComplete={handleUploadComplete} />
        )}

        {step === "processing" && (
          <StatusTracker
            jobId={jobId}
            onComplete={handleJobComplete}
            onReset={handleReset}
          />
        )}

        {step === "results" && jobData && (
          <div className="results-layout">
            <ScanViewer jobId={jobId} jobData={jobData} goToSlice={goToSlice} />
            <ResultsPanel jobData={jobData} onNewScan={handleReset} onSliceClick={(s) => setGoToSlice(s)} />
          </div>
        )}
      </div>

      {/* ── Show / Hide Metrics ───────────────────────────────────────────── */}
      {step === "results" && (
        <div style={{ textAlign: "center", marginTop: "1.5rem" }}>
          <div
            className="button cust-btn-2"
            onClick={() => setShowMetrics((prev) => !prev)}
          >
            {showMetrics ? "Hide Evaluation Metrics" : "See Evaluation Metrics"}
          </div>
        </div>
      )}

      {/* ── Evaluation Metrics ────────────────────────────────────────────── */}
      {showMetrics && (
        <div className="row mt-4">
          <p>
            <ul style={{ listStyle: "none", textAlign: "left" }}>
              <li><strong>Notes:</strong></li>
              <li><em>Dice Score</em> measures overlap between predicted and actual nodule masks.</li>
              <li><em>Precision</em> shows how many predicted nodule pixels are correct.</li>
              <li><em>Recall</em> indicates how many true nodule pixels the model captures.</li>
              <li><em>F1 Score</em> balances precision and recall.</li>
              <li><em>IoU</em> (Intersection over Union) measures segmentation quality.</li>
              <li><em>FROC (CPM)</em> is the LUNA16 competition metric for nodule detection.</li>
            </ul>
          </p>
          {LUNG_METRICS.map((m) => (
            <div key={m.label} className="col-sm-2">
              <div className="card">
                <div className="card-body">
                  <div className="row">
                    <div className="col mt-0">
                      <h5 className="card-title">{m.label}</h5>
                    </div>
                  </div>
                  <h1 className="mt-1 mb-3">{m.value}%</h1>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Ask Isha Chat ─────────────────────────────────────────────────── */}
      <IshaChat cancerName="Lung Cancer" />
    </>
  );
}
